import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { apolloClient } from "../../apollo/graphql";
import { GET_ACTIVE_SESSIONS, GET_EXPIRED_SESSIONS } from "../../apollo/queries";
import { RejectWithValueType } from "../auth/types/authType";

export interface ActiveSession {
  _id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  companyName?: string;
  userAgent?: string;
  ip?: string;
  loginAt: string;
  lastActivityAt: string;
}

export interface ExpiredSession extends ActiveSession {
  revokedAt?: string;
  revokedReason?: string;
}

export interface ActiveSessionsWithCount {
  totalCount: number;
  sessions: ActiveSession[];
}

export interface ExpiredSessionsWithCount {
  totalCount: number;
  sessions: ExpiredSession[];
}

export interface SessionArgs {
  searchText?: string;
  skip: number;
  take: number;
}

interface SessionsState {
  sessionsWithCount: ActiveSessionsWithCount;
  loading: "idle" | "pending";
  expiredSessionsWithCount: ExpiredSessionsWithCount;
  expiredLoading: "idle" | "pending";
}

export const fetchActiveSessions = createAsyncThunk<
  any,
  SessionArgs,
  { rejectValue: RejectWithValueType }
>("sessions/fetchActiveSessions", async (args, thunkAPI) => {
  const { rejectWithValue } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_ACTIVE_SESSIONS,
      variables: { ...args },
      fetchPolicy: "network-only",
    });
    if (response && response.data && response.data.ActiveSessions) {
      return response.data.ActiveSessions as ActiveSessionsWithCount;
    }
  } catch (error: any) {
    return rejectWithValue({ message: error.message });
  }
});

export const fetchExpiredSessions = createAsyncThunk<
  any,
  SessionArgs,
  { rejectValue: RejectWithValueType }
>("sessions/fetchExpiredSessions", async (args, thunkAPI) => {
  const { rejectWithValue } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_EXPIRED_SESSIONS,
      variables: { ...args },
      fetchPolicy: "network-only",
    });
    if (response && response.data && response.data.ExpiredSessions) {
      return response.data.ExpiredSessions as ExpiredSessionsWithCount;
    }
  } catch (error: any) {
    return rejectWithValue({ message: error.message });
  }
});

const initialState: SessionsState = {
  sessionsWithCount: { totalCount: 0, sessions: [] },
  loading: "idle",
  expiredSessionsWithCount: { totalCount: 0, sessions: [] },
  expiredLoading: "idle",
};

export const sessionsSlice = createSlice({
  name: "sessions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchActiveSessions.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchActiveSessions.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.sessionsWithCount = payload;
    });
    builder.addCase(fetchActiveSessions.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(fetchExpiredSessions.pending, (state) => {
      state.expiredLoading = "pending";
    });
    builder.addCase(fetchExpiredSessions.fulfilled, (state, { payload }) => {
      state.expiredLoading = "idle";
      state.expiredSessionsWithCount = payload;
    });
    builder.addCase(fetchExpiredSessions.rejected, (state) => {
      state.expiredLoading = "idle";
    });
  },
});

export default sessionsSlice.reducer;

export const selectSessions = (state: RootState) =>
  state.sessions as SessionsState;
